<template>
    <card title="AR Reports">
        <b-container fluid>
            <div v-if="!arEnabled" class="text-center p-3 text-muted">
                Accounts Receivable is not enabled for this pharmacy.
            </div>
            <template v-else>
                <b-row class="align-items-end mb-2">
                    <b-col cols="4">
                        <label class="mb-1">Report</label>
                        <b-select v-model="selectedKey" :options="reportOptions" @change="onReportChange" />
                    </b-col>

                    <b-col cols="2" v-if="paramType === 'asOf'">
                        <label class="mb-1">As Of</label>
                        <b-form-datepicker v-model="asOf" />
                    </b-col>

                    <template v-if="paramType === 'range'">
                        <b-col cols="2">
                            <label class="mb-1">From</label>
                            <b-form-datepicker v-model="fromDate" />
                        </b-col>
                        <b-col cols="2">
                            <label class="mb-1">To</label>
                            <b-form-datepicker v-model="toDate" />
                        </b-col>
                    </template>

                    <b-col cols="2" v-if="hasMethodFilter">
                        <label class="mb-1">Method</label>
                        <b-select v-model.number="paymentMethod" :options="methodFilterOptions" />
                    </b-col>

                    <b-col cols="2" v-if="paramType === 'minBalance'">
                        <label class="mb-1">Min Balance</label>
                        <b-form-input v-model.number="minBalance" type="number" min="0" step="0.01" />
                    </b-col>

                    <b-col class="text-right">
                        <b-button variant="primary" class="mr-2" :disabled="loading" @click="run">Run</b-button>
                        <b-button variant="outline-secondary" :disabled="loading || !rows.length" @click="exportCsv">
                            Export CSV
                        </b-button>
                    </b-col>
                </b-row>

                <b-table :fields="fields" :items="rows" :busy="loading" small responsive show-empty>
                    <template #table-busy>
                        <div class="text-center my-2"><b-spinner small /> Loading...</div>
                    </template>
                    <template #empty>No data. Choose a report and click Run.</template>
                </b-table>
            </template>
        </b-container>
    </card>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import {
    arAccountStatusLabel,
    arPaymentMethodLabel,
    arPaymentStatusLabel,
    arTransactionTypeLabel,
} from "@/models/AR";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

interface ReportField {
    key: string;
    label: string;
    formatter?: (value: any) => string;
}

interface ReportConfig {
    key: string;
    label: string;
    endpoint: string;
    paramType: "asOf" | "range" | "minBalance";
    hasMethodFilter?: boolean;
    fields: ReportField[];
}

@Component({
    name: "ARReportsPage",
})
export default class ARReportsPage extends Vue {
    private arEnabled = false;
    private loading = false;
    private rows: any[] = [];

    private selectedKey = "aging-summary";
    private asOf: string = moment().format("YYYY-MM-DD");
    private fromDate: string = moment().subtract(1, "month").format("YYYY-MM-DD");
    private toDate: string = moment().format("YYYY-MM-DD");
    private minBalance = 0;
    private paymentMethod: number | null = null;

    private methodFilterOptions = [
        { value: null, text: "All Methods" },
        { value: 1, text: "Cash" },
        { value: 2, text: "Check" },
        { value: 3, text: "Credit Card" },
    ];

    private get money() {
        return (v: number) => this.currency(v);
    }
    private get date() {
        return (v: string) => this.formatDate(v);
    }

    get reportConfigs(): ReportConfig[] {
        const money = this.money;
        const date = this.date;
        return [
            {
                key: "aging-summary", label: "Aging Summary", endpoint: "/AR/reports/aging-summary", paramType: "asOf",
                fields: [
                    { key: "accountNumber", label: "Account" },
                    { key: "responsiblePatientId", label: "Patient" },
                    { key: "status", label: "Status", formatter: arAccountStatusLabel },
                    { key: "balance", label: "Balance", formatter: money },
                    { key: "agingCurrent", label: "Current", formatter: money },
                    { key: "agingBucket1", label: "31-60", formatter: money },
                    { key: "agingBucket2", label: "61-90", formatter: money },
                    { key: "agingBucket3", label: "91-120", formatter: money },
                    { key: "agingBucket4Plus", label: "120+", formatter: money },
                    { key: "total", label: "Total", formatter: money },
                ],
            },
            {
                key: "aging-detail", label: "Aging Detail", endpoint: "/AR/reports/aging-detail", paramType: "asOf",
                fields: [
                    { key: "accountNumber", label: "Account" },
                    { key: "chargeTransactionId", label: "Txn" },
                    { key: "orderId", label: "Order" },
                    { key: "patientId", label: "Patient" },
                    { key: "originalAmount", label: "Original", formatter: money },
                    { key: "outstanding", label: "Outstanding", formatter: money },
                    { key: "ageDays", label: "Age (days)" },
                    { key: "bucket", label: "Bucket" },
                ],
            },
            {
                key: "payment-history", label: "Payment History", endpoint: "/AR/reports/payment-history",
                paramType: "range", hasMethodFilter: true,
                fields: [
                    { key: "paymentId", label: "Payment" },
                    { key: "accountNumber", label: "Account" },
                    { key: "amount", label: "Amount", formatter: money },
                    { key: "paymentMethod", label: "Method", formatter: arPaymentMethodLabel },
                    { key: "checkNumber", label: "Check #" },
                    { key: "status", label: "Status", formatter: arPaymentStatusLabel },
                    { key: "created", label: "Date", formatter: date },
                    { key: "createdBy", label: "By" },
                ],
            },
            {
                key: "statement-run-history", label: "Statement Run History",
                endpoint: "/AR/reports/statement-run-history", paramType: "range",
                fields: [
                    { key: "id", label: "Statement" },
                    { key: "accountNumber", label: "Account" },
                    { key: "statementDate", label: "Date", formatter: date },
                    { key: "periodStart", label: "Period Start", formatter: date },
                    { key: "periodEnd", label: "Period End", formatter: date },
                    { key: "endingBalance", label: "Ending Balance", formatter: money },
                    { key: "createdBy", label: "By" },
                ],
            },
            {
                key: "adjustment-write-off-audit", label: "Adjustment / Write-Off Audit",
                endpoint: "/AR/reports/adjustment-write-off-audit", paramType: "range",
                fields: [
                    { key: "id", label: "Txn" },
                    { key: "accountNumber", label: "Account" },
                    { key: "transactionType", label: "Type", formatter: arTransactionTypeLabel },
                    { key: "amount", label: "Amount", formatter: money },
                    { key: "reasonCode", label: "Reason" },
                    { key: "notes", label: "Notes" },
                    { key: "created", label: "Date", formatter: date },
                    { key: "createdBy", label: "By" },
                ],
            },
            {
                key: "open-balances", label: "Open Balances by Responsible Party",
                endpoint: "/AR/reports/open-balances", paramType: "minBalance",
                fields: [
                    { key: "responsiblePatientId", label: "Responsible Patient" },
                    { key: "accountCount", label: "Accounts" },
                    { key: "totalBalance", label: "Total Balance", formatter: money },
                ],
            },
            {
                key: "past-due", label: "Past-Due Accounts", endpoint: "/AR/past-due", paramType: "asOf",
                fields: [
                    { key: "accountNumber", label: "Account" },
                    { key: "responsiblePatientId", label: "Patient" },
                    { key: "balance", label: "Balance", formatter: money },
                    { key: "pastDueTotal", label: "Past Due", formatter: money },
                ],
            },
            {
                key: "audit-trail", label: "Audit Trail", endpoint: "/AR/reports/audit-trail", paramType: "range",
                fields: [
                    { key: "eventTime", label: "Time", formatter: (v: string) => this.formatDateTime(v) },
                    { key: "eventType", label: "Event" },
                    { key: "entityId", label: "Entity" },
                    { key: "user", label: "User" },
                    { key: "amount", label: "Amount", formatter: money },
                    { key: "reasonCode", label: "Reason" },
                    { key: "detail", label: "Detail" },
                ],
            },
        ];
    }

    get reportOptions() {
        return this.reportConfigs.map(c => ({ value: c.key, text: c.label }));
    }

    get selectedConfig(): ReportConfig {
        return this.reportConfigs.find(c => c.key === this.selectedKey) ?? this.reportConfigs[0];
    }

    get paramType(): string {
        return this.selectedConfig.paramType;
    }

    get hasMethodFilter(): boolean {
        return !!this.selectedConfig.hasMethodFilter;
    }

    get fields() {
        return this.selectedConfig.fields.map(f => ({ key: f.key, label: f.label, formatter: f.formatter, sortable: true }));
    }

    async mounted(): Promise<void> {
        try {
            const response = await axios.get<boolean>("/configuration/enableAR");
            this.arEnabled = response.data === true;
        } catch {
            this.arEnabled = false;
        }
    }

    onReportChange(): void {
        this.rows = [];
    }

    async run(): Promise<void> {
        const cfg = this.selectedConfig;
        const params: Record<string, any> = {};
        if (cfg.paramType === "asOf") params.asOf = this.asOf;
        if (cfg.paramType === "range") {
            params.from = this.fromDate;
            params.to = this.toDate;
            if (cfg.hasMethodFilter && this.paymentMethod != null) params.paymentMethod = this.paymentMethod;
        }
        if (cfg.paramType === "minBalance") params.minBalance = this.minBalance;

        try {
            this.loading = true;
            const response = await axios.get<any[]>(cfg.endpoint, { params });
            this.rows = response.data ?? [];
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            this.rows = [];
        } finally {
            this.loading = false;
        }
    }

    exportCsv(): void {
        const cfg = this.selectedConfig;
        const escape = (value: string) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
        const header = cfg.fields.map(f => escape(f.label)).join(",");
        const lines = this.rows.map(row =>
            cfg.fields.map(f => {
                let cell = row[f.key];
                if (f.formatter) cell = f.formatter(cell);
                return escape(cell == null ? "" : String(cell));
            }).join(",")
        );
        const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `ar-${cfg.key}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }

    formatDate(date?: string): string {
        return date ? moment.utc(date).local().format("L") : "";
    }

    formatDateTime(date?: string): string {
        return date ? moment.utc(date).local().format("L LT") : "";
    }
}
</script>

<style scoped></style>
